import React, { useEffect, useState } from 'react';
import { cn } from '@shared/lib/utils';

interface PreloaderProps {
  progress: number;
  status: string;
  isComplete: boolean;
  onComplete?: () => void;
}

export const Preloader: React.FC<PreloaderProps> = ({
  progress,
  status,
  isComplete,
  onComplete,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  useEffect(() => {
    if (isComplete && !isAnimatingOut) {
      setIsAnimatingOut(true);
      // Wait for animation to complete before calling onComplete
      const timer = setTimeout(() => {
        setIsVisible(false);
        onComplete?.();
      }, 150); // Much faster transition

      return () => clearTimeout(timer);
    }
  }, [isComplete, isAnimatingOut, onComplete]);

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex flex-col items-center justify-center bg-white transition-opacity duration-150',
        isAnimatingOut ? 'opacity-0' : 'opacity-100'
      )}
    >
      {/* Logo or Brand */}
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold text-gray-900 md:text-6xl">
          Krasota
        </h1>
        <p className="mt-2 text-lg text-gray-600">Коммерческая недвижимость</p>
      </div>

      {/* Progress Bar */}
      <div className="w-80 max-w-md">
        <div className="mb-4 flex justify-between text-sm text-gray-600">
          <span>{status}</span>
          <span>{Math.round(progress)}%</span>
        </div>

        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="progress-bar h-full bg-gradient-to-r from-blue-500 to-purple-600"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Loading Animation */}
      <div className="mt-8 flex space-x-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="loading-dot h-3 w-3 rounded-full bg-blue-500"
          />
        ))}
      </div>

      {/* Status Messages */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-500">
          {progress < 30 && 'Загружаем критические ресурсы...'}
          {progress >= 30 && progress < 60 && 'Получаем данные с сервера...'}
          {progress >= 60 && progress < 90 && 'Подготавливаем интерфейс...'}
          {progress >= 90 && progress < 100 && 'Почти готово...'}
          {progress >= 100 && 'Готово!'}
        </p>
      </div>
    </div>
  );
};
